import { useState, useEffect } from "react";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AiOutlineBell, AiOutlineCaretDown, AiOutlineCloudDownload, AiOutlineMail } from "react-icons/ai";
import { HiArrowLeft, HiOutlineBriefcase, HiOutlineCalendarDays, HiOutlineSquares2X2 } from "react-icons/hi2";
import SendFromMyEmailModal from "../../components/send-from-my-email/send-from-my-email-modal";
import { RiSendPlaneLine } from "react-icons/ri";
import { HiOutlineLink } from "react-icons/hi";
import { copyTestLink, getEmailStatusInfo, isTestFinished } from "../../../utils/test-invitation-link";
import { toast, Toaster } from "react-hot-toast";
import moment from "moment";
import { formatRut } from "rutlib";

import { statusColors, statusTranslator } from "../../../helpers/functions";
import { collaboratorTestsSelector, useCollaboratorsActions } from "../../../services/actions/collaborators.actions";
import DropdownActions from "../../../components/dropdown/dropdown-actions";
import { HOST_PUBLIC } from "../../../utils/axios";
import LocalTable from "../../../components/table-local/index";
import { CollaboratorInfo, CollaboratorResults } from "./components/folder-content";
import EditCollaboratorModal from "./components/edit-modal";
import { userAtom } from "../../../services/atoms/auth.atoms";
import { AVAILABLE_TESTS, ERROR_MESSAGES } from "../../../helpers/constants";
import { TestTypeService } from "../../../utils/test-type-registry";
import InvitationModal from "../clients-details/components/invitation-modal";
import IAIcon from '../../../assets/creation.png';
import IaInfoModal from "./components/ia-info-modal";
import IaSummaryModal from "./components/ia-summary-modal";
import AnswersModal from "./components/answers-modal";
import { useAuthActions } from "../../../services/actions/auth.actions";
import { useAnswersAction } from "../../../services/actions/answers.action";
import { answersAtom } from "../../../services/atoms/answers.atom";

const initialCollaboratorData = {
  name: "",
  surname: "",
  rut: "",
  email: "",
  area: "",
  position: "",
}

const badgeAccessor = (row) => (
  <div className={`gm-badge gm-badge--${statusColors(row)}`}>
    <span>{statusTranslator(row)}</span>
  </div>
);

const dateFotmatter = (date) => <div>{date ? `${moment(date).format('DD-MM-YYYY HH:mm')} hrs` : '-'}</div>;

const options = [
  { id: 'opt-1', title: 'Descargar Informe', action: 'download', icon: AiOutlineCloudDownload },
  { id: 'opt-2', title: 'Enviar Invitación', action: 'send', icon: RiSendPlaneLine },
  // { id: 'opt-3', title: 'Editar Colaborador', action: 'edit', icon: HiOutlinePencil },
];

const optionsAdminOperative = [
  { id: 'opt-2', title: 'Enviar Invitación', action: 'send', icon: RiSendPlaneLine },
];

// Fecha de envío y, debajo, qué pasó con el correo según SES (entregado, rebotó...).
const notificationAccessor = (testRow) => {
  const email = getEmailStatusInfo(testRow);
  return (
    <div>
      {dateFotmatter(testRow.sentAt)}
      {email && (
        <small
          className={`gm-badge-hint ${email.problem ? 'gm-badge-hint--error' : 'gm-badge-hint--ok'}`}
          title={email.tip}
        >
          {email.label}
        </small>
      )}
    </div>
  );
};

// Invitación o recordatorio desde el correo de quien está en el panel, o el link directo
// del test pendiente. stopPropagation evita que el clic seleccione la fila.
const linkActions = (testRow, collaborator, openMyEmail) => {
  if (isTestFinished(testRow)) return <div>-</div>;
  return (
    <div className="gm-test-link-actions">
      <button
        type="button"
        title="Enviar la invitación al test desde tu correo"
        onClick={(event) => {
          event.stopPropagation();
          openMyEmail({ collaboratorTestId: testRow.id, name: collaborator?.name, email: collaborator?.email, kind: 'invitation' });
        }}
      >
        <AiOutlineMail /> Enviar invitación
      </button>
      <button
        type="button"
        title="Enviar un recordatorio desde tu correo"
        onClick={(event) => {
          event.stopPropagation();
          openMyEmail({ collaboratorTestId: testRow.id, name: collaborator?.name, email: collaborator?.email, kind: 'reminder' });
        }}
      >
        <AiOutlineBell /> Recordatorio
      </button>
      <button
        type="button"
        title="Copiar link del test"
        onClick={(event) => { event.stopPropagation(); copyTestLink(testRow.id); }}
      >
        <HiOutlineLink /> Copiar link
      </button>
    </div>
  );
};

const buildColumns = (collaborator, openMyEmail) => [
  // { Header: 'ID', accessor: 'id', disableSortBy: true },
  { Header: 'Evaluación', accessor: 'testName', disableSortBy: true },
  { Header: 'Estado', accessor: ({ statusId }) => badgeAccessor(statusId), disableSortBy: true },
  { Header: 'Notificación', id: 'notification', accessor: (testRow) => notificationAccessor(testRow), disableSortBy: true },
  { Header: 'Realizado el', accessor: ({ finishedAt }) => dateFotmatter(finishedAt) },
  { Header: 'Link del test', id: 'test-link', accessor: (testRow) => linkActions(testRow, collaborator, openMyEmail), disableSortBy: true },
];

const CollaboratorDetails = () => {
  const user = useRecoilValue(userAtom);
  const answersState = useRecoilValue(answersAtom);
  let { id: collaboratorId } = useParams();
  let navigate = useNavigate();
  let { state } = useLocation();
  const { buildIaSummary } = useCollaboratorsActions();
  const { refreshToken } = useAuthActions();
  const { updateCollaborator } = useCollaboratorsActions();
  const { getAnswers, resetAnswers } = useAnswersAction();
  const [open, setOpen] = useState(false);
  const [test, setTest] = useState(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [showIaInfoModal, setShowIaInfoModal] = useState(false);
  const [showIaSummaryModal, setShowIaSummaryModal] = useState(false);
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  // Ventana "Enviar invitación / recordatorio" (null = cerrada).
  const [myEmailTarget, setMyEmailTarget] = useState(null);
  const [collaborator, setCollaborator] = useState(initialCollaboratorData);
  const [isInternal, setInternal] = useState(true);
  const [iaSummary, setIaSummary] = useState("");
  const [mustBeShowGrowmeIaAction, setMustBeShowGrowmeIaAction] = useState("");
  const [mustBeShowAnswersAction, setMustBeShowAnswersAction] = useState(false);

  const collaboratorTests = useRecoilValueLoadable(collaboratorTestsSelector(state?.collaborator?.id || collaboratorId));

  const goBack = () => navigate(-1);
  const handleOptionAction = (e) => {
    if (e === 'edit') setOpen(true);
    if (e === 'download') {
      if (!test?.id) {
        toast.error('El informe aún no está disponible. Recarga la página e inténtalo de nuevo.');
        return;
      }
      window.open(`${HOST_PUBLIC}/${test.id}.pdf`, '_blank');
    }
    if (e === 'send') {
      setShowInvitationModal(true);
    }
  };

  const handleOpenInvitationModal = () => {
    setShowInvitationModal(!showInvitationModal);
  }

  const handleOpenIaInfoModal = () => {
    setShowIaInfoModal(!showIaInfoModal);
  }
  
  const handleOpenIaSummaryModal = () => {
    setShowIaSummaryModal(!showIaSummaryModal);
  }

  const handleOpenModal = () => setOpen(!open);

  useEffect(() => {
    setCollaborator((prevValues) => ({ ...prevValues, ...collaboratorTests.contents }));
    if (collaboratorTests.contents?.tests) {
      const test = collaboratorTests.contents.tests.find((t) => (TestTypeService.isTestCategory(t, 'competency') || TestTypeService.isTestCategory(t, 'unified')) && t.results);
      const isCompetencyTest = !!test?.results;
      setShowIaInfoModal(isCompetencyTest);
      setMustBeShowGrowmeIaAction(isCompetencyTest);
      setMustBeShowAnswersAction(isCompetencyTest && user?.data?.isSuperAdmin);
      const testWithResults = collaboratorTests.contents.tests.find(t => t.results) || collaboratorTests.contents.tests[0];
      handleOpenDetails(testWithResults);
    }
  }, [collaboratorTests.state, collaboratorTests.contents]);

  const handleSubmitEdit = (e) => {
    e.preventDefault();
    e.preventDefault();
    const dataToSend={
      ...collaborator,
      collaboratorInternal: isInternal,
      collaboratorExternal: !isInternal, 
      rut: formatRut(collaborator.rut),
      phone: collaborator.phone.length >3 ? `+56${collaborator.phone}` : ""
    }
    updateCollaborator(dataToSend, () => {
      toast.success('Colaborador actualizado exitosamente');
      handleOpenModal();
    });
  }

  const getYearsInCompany = (startDate) => {
    // 1810-01-01 es la fecha "no informada" que guarda el back.
    if (!startDate || moment(startDate).year() <= 1810) return '';
    const years = moment().diff(moment(startDate), 'years');
    return `${years} ${years === 1 ? 'año' : 'años'} en la empresa`;
  };

  const isLessThanOneYear = (date) => {
    if (!date) return false;
    const years = moment().diff(moment(date), 'years');
    return years < 1;
  };

  const getTestDisc = () => {
    const testDiscAvailable = AVAILABLE_TESTS.find((test) => test.code === 'TestDisc');
    if (testDiscAvailable && collaboratorTests.contents?.tests) {
      return collaboratorTests.contents?.tests.find((test) => test.testId === testDiscAvailable.id);
    }
    return null;
  };

  const handleOpenDetails = (testSelected) => {
    let test = testSelected;
    if (TestTypeService.isTestCategory(testSelected, 'competency')) {
      const testDisc = getTestDisc();
      if (testDisc && testDisc.createdAt) {
        const testDiscDate = moment(testDisc.createdAt);
        const lessThanOneYear = isLessThanOneYear(testDiscDate);
        if (lessThanOneYear) {
          test = { ...testSelected, testDisc };
        }
      }
    }
    setTest(test);
  };

  const onClickGrowmeIa = () => {
    getIaSummaryAction();
  };

  const getIaSummaryAction = () => {
    const testForSummary = getTestForIaSummary();
    if (testForSummary) {
      setShowIaSummaryModal(true);
      getIaSummary(testForSummary);
    } else {
      toast.error('El colaborador no tiene pruebas disponibles con resultados para generar el resumen.');
    }
  }
  
  const onClickStartIaSummary = () => {
    setShowIaInfoModal(false);
    getIaSummaryAction();
  };

  // Prioritize unified test for IA summary, fallback to legacy Growme
  const getTestForIaSummary = () => {
    if (!collaboratorTests.contents?.tests) return null;
    // Prioritize unified test
    const unifiedTest = collaboratorTests.contents.tests.find(t => TestTypeService.isTestCategory(t, 'unified') && t.results);
    if (unifiedTest) return unifiedTest;
    // Fallback to competency test
    return collaboratorTests.contents.tests.find(t => TestTypeService.isTestCategory(t, 'competency') && t.results);
  };

  const getIaSummary = (test) => {
    setIaSummary("Cargando resumen...");
    buildIaSummary({
      collaboratorId: collaboratorId,
      collaboratorTestId: test.id,
    }, (res) => {
      if (res === 500) {
        toast.error('Error al obtener el resumen IA');
        return;
      }
      if (res === 401) {
        toast.error(ERROR_MESSAGES.session_expired);
        refreshToken();
        return;
      }
      setIaSummary(res.analysis || "Ocurrió un problema al obtener el resumen. Intenten más tarde.");
    });
  };

  const onSeeAnswersClick = () => {
    resetAnswers();
    setShowAnswersModal(true);
    getAnswers({
      collaboratorTestId: test?.id,
    });
  };

  const yearsInCompany = getYearsInCompany(collaborator?.startDate);

  const modalItems = collaboratorTests?.contents ? [collaboratorTests?.contents] : [];

  // Datos a mostrar: los del back apenas llegan; mientras, los que ya había.
  const collaboratorData = collaboratorTests.state === "hasValue" ? collaboratorTests.contents : collaborator;
  const fullName = `${collaboratorData?.name || ""} ${collaboratorData?.surname || ""}`.trim();
  const initials = fullName.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  const originLabel = collaboratorData?.collaboratorInternal
    ? "Colaborador interno"
    : collaboratorData?.collaboratorExternal ? "Colaborador externo" : "Colaborador";
  // Igual que antes: el menú de acciones aparece al llegar desde un cliente o una
  // evaluación, y Growme IA solo desde un cliente.
  const showActions = Boolean(state?.client || state?.charge);

  const handleToggleAnswersModal = () => {
    setShowAnswersModal(false);
  };

  return (
    <div className="gm-collaborator-details-container">
      <Toaster />
      <SendFromMyEmailModal target={myEmailTarget} onClose={() => setMyEmailTarget(null)} />
      <EditCollaboratorModal
        collaboratorHandler={{
          collaborator,
          setCollaborator,
          isInternal,
          setInternal,
        }}
        open={open}
        submit={handleSubmitEdit}
        toggle={handleOpenModal}
      />
      <header className="gm-ch">
        <div className="gm-ch-main">
          <span className="gm-avatar gm-avatar--lg" aria-hidden="true">{initials}</span>
          <div className="gm-ch-text">
            <div className="gm-eyebrow">{originLabel}</div>
            <h1>{fullName || "Colaborador"}</h1>
            <div className="gm-ch-meta">
              {collaboratorData?.position && <span><HiOutlineBriefcase size={15} />{collaboratorData.position}</span>}
              {collaboratorData?.area && <span><HiOutlineSquares2X2 size={15} />{collaboratorData.area}</span>}
              {yearsInCompany && <span><HiOutlineCalendarDays size={15} />{yearsInCompany}</span>}
            </div>
          </div>
        </div>
        {showActions && (
          <div className="gm-ch-actions">
            <button type="button" className="gm-ghost-button" onClick={goBack}>
              <HiArrowLeft size={16} /> {state?.charge || "Volver"}
            </button>
            <div className="gm-actions-dropdown">
              <DropdownActions
                options={user?.data?.isAdminBasic ? optionsAdminOperative : options}
                label="Acciones"
                handleOnClick={handleOptionAction}
                Icon={AiOutlineCaretDown}
              />
            </div>
            {state?.client && mustBeShowGrowmeIaAction &&
              <button
                className="gm-btn-ia"
                type="button"
                onClick={onClickGrowmeIa}>
                Growme IA
                <img src={IAIcon} alt="IA icon" />
              </button>
            }
          </div>
        )}
      </header>
      <div className="gm-collab-grid">
        <aside className="gm-panel">
          <div className="gm-panel-head"><h2>Información</h2></div>
          <div className="gm-panel-body">
            <CollaboratorInfo collaborator={collaboratorData} />
          </div>
        </aside>
        <div className="gm-collab-stack">
          <section className="gm-panel gm-panel--table">
            <div className="gm-panel-head"><h2>Tests</h2></div>
            <LocalTable
              columns={buildColumns(collaboratorData, setMyEmailTarget)}
              rowSelected={test}
              handleOpenDetails={handleOpenDetails}
              emptyListInformer="small"
              section={{ plural: "tests", singular: "test" }}
              data={
                collaboratorTests.state === "hasValue"
                  ? collaboratorTests.contents?.tests || []
                  : []
              }
              loading={collaboratorTests.state === "loading"}
            />
          </section>
          <CollaboratorResults
            test={test}
            mustBeShowAnswersAction={mustBeShowAnswersAction}
            onSeeAnswersClick={onSeeAnswersClick}
          />
        </div>
      </div>
      <InvitationModal
        open={showInvitationModal}
        toggle={handleOpenInvitationModal}
        checkedItems={modalItems}
        allItems={[]}
      />
      <IaInfoModal
        open={showIaInfoModal}
        toggle={handleOpenIaInfoModal}
        onClickStartIaSummary={onClickStartIaSummary}
      />
      <IaSummaryModal
        open={showIaSummaryModal}
        toggle={handleOpenIaSummaryModal}
        iaSummary={iaSummary}
      />
      <AnswersModal
        open={showAnswersModal}
        toggle={handleToggleAnswersModal}
        answers={answersState.answers}
        loading={answersState.loading}
      />
    </div>
  );
}

export default CollaboratorDetails;
